/**
 * G-72: reports that could not be sent, held on the device.
 *
 * WHY THIS EXISTS AT ALL. The report most worth having is the one filed when
 * something is already broken — and "the connection is down" is one of the
 * things that breaks. A reporter that silently drops those is a reporter that
 * fails exactly when it is needed. So an unsent report waits here and goes on
 * the next load, rather than the app pretending it was delivered.
 *
 * `localStorage`, not memory: the florist's next move after a crash is usually
 * to restart the app, which is precisely when a memory queue would be lost.
 *
 * NO SCREENSHOT IS QUEUED. An image is megabytes and `localStorage` is a ~5 MB
 * budget shared with everything else; one queued screenshot could evict the
 * printer registry and the dictation language. The message, the console tail
 * and the context are what make a report answerable — the picture is a bonus
 * that only survives when the report goes out immediately.
 */

const QUEUE_KEY = "fw-bug-report-queue";
/** A queue is a safety net, not a spool. Beyond this the oldest are dropped. */
const MAX_QUEUED = 10;

export interface QueuedBugReport {
  message: string;
  kind: "REPORTED" | "AUTOMATIC";
  screen?: string;
  consoleLog?: string;
  errorMessage?: string;
  errorStack?: string;
  appVersion?: string;
  userAgent?: string;
  viewportWidth?: number;
  viewportHeight?: number;
  /** When it actually happened, so a late delivery is not read as a late bug. */
  occurredAt: string;
}

function isQueued(value: unknown): value is QueuedBugReport {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.message === "string" && typeof v.occurredAt === "string";
}

export function readQueue(): QueuedBugReport[] {
  try {
    const raw = window.localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // A half-written or older-shaped entry degrades to "not queued" rather than
    // throwing on every app load forever.
    return parsed.filter(isQueued);
  } catch {
    return [];
  }
}

function writeQueue(reports: QueuedBugReport[]): void {
  try {
    window.localStorage.setItem(QUEUE_KEY, JSON.stringify(reports));
  } catch {
    // Storage full or blocked. Losing a queued report is bad; crashing the
    // screen that was already showing an error is worse.
  }
}

/**
 * Add one, keeping the NEWEST when the queue is full.
 *
 * Newest rather than oldest because a shop offline for an hour produces a run
 * of reports about the same outage, and the last one is the closest to whatever
 * state the tablet actually ended up in.
 */
export function enqueueBugReport(report: QueuedBugReport): QueuedBugReport[] {
  const next = [...readQueue(), report].slice(-MAX_QUEUED);
  writeQueue(next);
  return next;
}

export function clearBugReportQueue(): void {
  writeQueue([]);
}

/**
 * Try the queue, oldest first, and keep whatever still will not send.
 *
 * `send` throwing is the normal case, not an exception: it means the line is
 * still down. Everything from the first failure onward is kept, in order, so a
 * flapping connection cannot reorder a morning's reports.
 */
export async function flushBugReportQueue(
  send: (report: QueuedBugReport) => Promise<void>,
): Promise<{ sent: number; kept: number }> {
  const queued = readQueue();
  if (queued.length === 0) return { sent: 0, kept: 0 };

  let sent = 0;
  for (let i = 0; i < queued.length; i += 1) {
    try {
      await send(queued[i]);
      sent += 1;
    } catch {
      const kept = queued.slice(i);
      writeQueue(kept);
      return { sent, kept: kept.length };
    }
  }
  writeQueue([]);
  return { sent, kept: 0 };
}
